import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FavoriteService } from "@/services/favorite/favoriteService";
import {
  GetAllFavoritesResponse,
  AddFavoriteResponse,
  RemoveFavoriteResponse,
} from "@/types/favorite";
import { ApiError } from "@/types/carAuctionClient";

export const useFavorites = (userId?: string) => {
  const queryClient = useQueryClient();

  // Get all favorites for a user
  const favoritesQuery = useQuery<GetAllFavoritesResponse, ApiError>({
    queryKey: ["favorites", userId],
    queryFn: () => FavoriteService.getAllFavorites(userId as string),
    enabled: Boolean(userId),
    staleTime: 0 * 60 * 1000, // 5 minutes
    gcTime: 0 * 60 * 1000, // 10 minutes
  });

  // Add favorite mutation
  const addFavoriteMutation = useMutation({
    mutationFn: ({
      listingId,
      userId,
    }: {
      listingId: string;
      userId: string;
    }) => FavoriteService.addFavorite(listingId, userId),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ["favorites", variables.userId],
      });

      // Snapshot the previous values
      const previousFavorites =
        queryClient.getQueryData<GetAllFavoritesResponse>([
          "favorites",
          variables.userId,
        ]);

      // Return a context object with the snapshotted values
      return {
        previousFavorites,
      };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousFavorites) {
        queryClient.setQueryData(
          ["favorites", variables.userId],
          context.previousFavorites
        );
      }
    },
    onSuccess: (data, variables) => {
      // No need to update individual favorite status since we use favoritesIds
    },
    onSettled: (data, error, variables) => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({
        queryKey: ["favorites", variables.userId],
      });
    },
  });

  // Remove favorite mutation
  const removeFavoriteMutation = useMutation({
    mutationFn: ({
      listingId,
      userId,
    }: {
      listingId: string;
      userId: string;
    }) => FavoriteService.removeFavorite(listingId, userId),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ["favorites", variables.userId],
      });

      // Snapshot the previous values
      const previousFavorites =
        queryClient.getQueryData<GetAllFavoritesResponse>([
          "favorites",
          variables.userId,
        ]);

      // Optimistically update the full favorites list
      queryClient.setQueryData<GetAllFavoritesResponse>(
        ["favorites", variables.userId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            favorites: oldData.favorites.filter(
              (favorite) => favorite.id !== variables.listingId
            ),
          };
        }
      );

      // Return a context object with the snapshotted values
      return {
        previousFavorites,
      };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousFavorites) {
        queryClient.setQueryData(
          ["favorites", variables.userId],
          context.previousFavorites
        );
      }
    },
    onSuccess: (data, variables) => {
      // No need to update individual favorite status since we use favoritesIds
    },
    onSettled: (data, error, variables) => {
      // Always refetch favorites when removing to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: ["favorites", variables.userId],
      });
    },
  });

  // Wrapper functions that accept callbacks
  const addFavorite = (
    variables: { listingId: string; userId: string },
    callbacks?: {
      onSuccess?: (data: AddFavoriteResponse) => void;
      onError?: (error: unknown) => void;
    }
  ) => {
    addFavoriteMutation.mutate(variables, {
      onSuccess: (data) => {
        callbacks?.onSuccess?.(data);
      },
      onError: (error) => {
        callbacks?.onError?.(error);
      },
    });
  };

  const removeFavorite = (
    variables: { listingId: string; userId: string },
    callbacks?: {
      onSuccess?: (data: RemoveFavoriteResponse) => void;
      onError?: (error: unknown) => void;
    }
  ) => {
    removeFavoriteMutation.mutate(variables, {
      onSuccess: (data) => {
        callbacks?.onSuccess?.(data);
      },
      onError: (error) => {
        callbacks?.onError?.(error);
      },
    });
  };

  return {
    // Data
    favorites: favoritesQuery.data?.favorites || [],
    totalCount: favoritesQuery.data?.totalCount || 0,

    // Loading states
    isLoadingFavorites: favoritesQuery.isLoading,
    isAddingFavorite: addFavoriteMutation.isPending,
    isRemovingFavorite: removeFavoriteMutation.isPending,

    // Error states
    favoritesError: favoritesQuery.error,
    addFavoriteError: addFavoriteMutation.error,
    removeFavoriteError: removeFavoriteMutation.error,

    // Actions
    addFavorite: addFavoriteMutation.mutateAsync,
    removeFavorite: removeFavoriteMutation.mutateAsync,

    // Refetch functions
    refetchFavorites: favoritesQuery.refetch,
  };
};
